export default {
  lang: {
    en: 'EN',
    zh: '中文',
  },

  nav: {
    products: '产品',
    mission: '服务',
    stories: '案例',
    contact: '联系我们',
    exploreProducts: '探索产品',
  },

  hero: {
    badge: '构建未来',
    title: '从创新想法到 AI 原生产品。',
    subtitle: 'AI Infra × AI Builder = 10× 效率',
    description:
      '基于 JIKER 构建的 AI Infra（基础设施）和培养的 AI Builder（人才），以 10× 效率把一个想法变成真正的 AI Native 业务，服务金融、能源、电商、教育、运营商等行业。',
    ctaPrimary: '探索产品',
    ctaSecondary: '联系我们',
  },

  products: {
    title: '核心产品',
    description:
      '两大基础：赋能应用的 AI Infra，和培养 AI Builder 的成长体系。',
    aiInfra: {
      title: 'AI Infra',
      description:
        '支撑每一款 AI 原生应用运行的对话、执行、路由与部署层。',
      looploop: {
        name: 'looploop 系列',
        description: '围绕对话、执行与应用运行时的 Agent 驱动产品。',
        items: [
          {
            title: 'looploop.chat',
            subtitle: '产品定义平台',
            description:
              '连接产品顾问与创新者，把模糊想法梳理成可执行的产品定义、技术路线和下一步行动。',
            tags: ['产品定义', '需求梳理', '可行性评估'],
            variant: 'primary',
          },
          {
            title: 'looploop.run',
            subtitle: '多 Agent 协作',
            description:
              '让 Agent 成为团队智能伙伴，支持多人、多 Agent、多机器实时协作，数据本地安全运行。',
            tags: ['多 Agent 协作', '本地执行', '团队工作空间'],
            variant: 'primary',
          },
          {
            title: 'looploop.vip',
            subtitle: 'Agent 运行时',
            description:
              '一键发布即获生产级运行环境，让 Agent 在真实世界里持续运行、自我纠偏并产生价值。',
            tags: ['Agent 运行时', '真实世界闭环', '持续运营'],
            variant: 'primary',
          },
        ],
      },
      llmpool: {
        name: 'llmpool 系列',
        description: '面向多模型 AI 系统的统一模型池与路由层。',
        items: [
          {
            title: 'llmpool.io',
            description:
              '统一管理多模型、分配流量并优化成本，通过一套 API 调用多种大模型。',
            tags: ['LLM 路由', 'API'],
            variant: 'lavender',
          },
        ],
      },
    },
    talentInfra: {
      title: 'AI Builder',
      description:
        '持续选拔和培养能把真实业务场景变成可用 AI 产品的 AI Builder。',
      items: [
        {
          title: '极客学院',
          description:
            '面向 AI Builder 新人的线上基础培训：概念、工具和工作流。',
          tags: ['线上', '培训'],
          variant: 'red',
        },
        {
          title: '极客未来学校',
          description:
            '高阶 AI Builder 线下生态，真实项目、导师指导和社区支持。',
          tags: ['线下', '项目'],
          variant: 'red',
        },
      ],
    },
  },

  stories: {
    title: '用 JIKER 构建',
    description:
      '基于 looploop、llmpool 和我们的 AI Builder 落地的真实 AI Native Agent 与 App。',
    energy: {
      title: '能源行业',
      items: [
        {
          title: '新能源运维 Agent',
          category: '新能源',
          description:
            '监控现场设备、回答运维问题并自动生成检修报告，服务于光伏与储能资产。',
          result: '检修报告产出：4 小时 → 20 分钟',
        },
        {
          title: '电网调度助手',
          category: '电网',
          description:
            '综合规程、故障日志与设备手册进行推理，帮助电网调度员加速决策。',
          result: '故障研判准备：2 天 → 3 小时',
        },
        {
          title: '安全智脑 Agent',
          category: '电网',
          description:
            '基于 LLM + VLM 的工作票和作业票智能安全督查应用，自动识别风险并辅助安监决策。',
          result: '票证审核：人工抽检 → 全量自动',
        },
        {
          title: '工程合规 Agent',
          category: '电网',
          description:
            '对照规程核查工程资料、许可手续与现场作业，降低合规风险。',
          result: '合规问题检出率提升 3 倍',
        },
      ],
    },
    ecommerce: {
      title: '电商行业',
      items: [
        {
          title: '电商客服 Agent',
          category: '客服',
          description:
            '面向电商客服团队的 AI 助手，自动接待咨询、推荐商品并处理售后问题。',
          result: '首次响应：45 秒 → 3 秒',
        },
        {
          title: '电商客户运营 Agent',
          category: '客户运营',
          description:
            '智能 CRM，分析客户对话、标注意图并自动化跟进，提升网店转化。',
          result: '沉默客户召回率提升 28%',
        },
      ],
    },
    finance: {
      title: '金融行业',
      items: [
        {
          title: '投研分析 Agent',
          category: '金融',
          description:
            '聚合研报、公告与行情数据，辅助分析师更快产出合规的投研初稿。',
          result: '研报初稿产出：2 天 → 40 分钟',
        },
        {
          title: '信贷尽调 Agent',
          category: '金融',
          description:
            '解析企业财报、征信记录与经营流水，为信贷审批提炼尽调要点与风险提示。',
          result: '单笔尽调周期：3 周 → 4 天',
        },
      ],
    },
    education: {
      title: '教育行业',
      items: [
        {
          title: 'AI 教研助手',
          category: '教育',
          description:
            '帮助教研团队生成教案、批改作业并沉淀校本知识库。',
          result: '单课备课时间减少 35%',
        },
        {
          title: '编程助教 Agent',
          category: '教育',
          description:
            '为学员提供全天候编程答疑、代码评审与个性化学习路径建议。',
          result: '作业反馈周期：2 天 → 10 分钟',
        },
      ],
    },
    exploring: {
      title: '探索中',
      items: [
        {
          title: 'My Twin',
          category: 'AI 数字分身',
          description:
            '语音优先的 AI 数字分身，与客户访谈、梳理需求，把口头想法转化为产品 brief。',
          result: '访谈到 brief：2 周 → 2 天',
        },
      ],
    },
  },

  mission: {
    title: '我们的服务',
    description:
      'JIKER 通过构建 AI Infra、培养 AI Builder、创造行业应用，让每一个人才都能创造更大价值。',
    items: [
      {
        title: '构建 AI Infra',
        description:
          '我们搭建对话、执行、模型路由与部署等基础工具，让每个 AI 原生应用都能站得住。',
      },
      {
        title: '培养 AI Builder',
        description:
          '极客学院与极客未来学校培养 AI Builder，让他们能利用我们的 Infra 把业务场景变成可用产品。',
      },
      {
        title: '创造应用',
        description:
          '有了基础设施和人才，我们持续为新能源、电网等行业构建 AI Native Agent 与 App。',
      },
    ],
  },

  about: {
    title: '极客',
    subtitle: '永不设限，探索无限',
    description:
      '一群颠覆传统、追求极致、自信乐享的探索家。',
    items: [
      {
        title: '颠覆传统',
        description:
          '以开拓精神挑战旧有的软件构建方式，用 AI 探索全新的可能。',
      },
      {
        title: '追求极致',
        description:
          '匠心质造、简约纯粹、精益求精。我们不止于「能用」，而是持续打磨到 exceptional。',
      },
      {
        title: '自信乐享',
        description:
          '自由学习、挚友伙伴、社交价值。我们作为一个 AI Builder 共同体一起成长。',
      },
    ],
  },

  cta: {
    title: '准备好用 AI 构建了吗？',
    description:
      '告诉我们你的行业场景，或加入未来学校成为一名 AI Builder。',
    primary: '联系我们',
  },

  footer: {
    description:
      '基于 JIKER 构建 AI Infra 与培养 AI Builder，以 10× 效率为各行业交付 AI Native 应用。',
    productsTitle: '产品',
    connectTitle: '联系我们',
    email: "邮箱",
    github: 'GitHub',
    twitter: 'Twitter / X',
    copyright: '© {year} JIKER. 保留所有权利。',
    tagline: '让人才更有价值',
  },
};
